import dayjs, { Dayjs } from 'dayjs';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { DatePickerValidationReason } from '../../components/date-picker/CommonDatePicker';
import { openToast } from '../../store/reducers/toastSlice';
import { invalidDatePickerUserMessage } from '../../utils/date-picker-invalid-date';

import type { ContractApiResponse } from '../../services/contract-services';
import type { OpenInvoiceApiResponse } from '../../services/payment-services';

/** For consumer loan only: 'invoice' = pay due amount; 'other' = under/over payment (editable amount). */
export type AmountChoice = 'invoice' | 'other';

export type PaymentMethodType = 'card' | 'bank_account';

export interface PaymentRow {
    contractId: string; // Aspire contract ID for display
    dbContractId: string; // Database contract ID for API
    bankAccount: string;
    paymentCardId: string; // Stripe payment method id when paying by card
    paymentDate: Dayjs | null;
    paymentAmount: number | string;
    dueAmount: string | number; // Outstanding balance from open invoices (read-only)
    scheduledAmount: string | number; // Amount already scheduled for payment (read-only)
    /** Consumer loan only: whether to pay invoice amount or other (editable) amount. */
    amountChoice?: AmountChoice;
}

interface UsePaymentRowsParams {
    activeContracts: ContractApiResponse[];
    getContractById: (contractId: string) => ContractApiResponse | null;
    getTotalAmountForContract: (contractId: string) =>
        | {
              totalAmount: string;
              dbContractId: string;
              invoiceCount: number;
          }
        | undefined;
    getScheduledAmountForContract: (contractId: string) => string;
    prefillAmounts: Record<string, string>;
    /**
     * When a single invoice was chosen (e.g. clicking "Pay Now" on one invoice row rather than
     * "Pay All"), overrides the contract-wide dueAmount/scheduledAmount with that invoice's own
     * amounts, so the make-payment screen reflects just that invoice instead of the whole contract.
     */
    singleInvoiceOverrides?: Record<string, { dueAmount: string; scheduledAmount: string }>;
    isConsumerLoanContract: (contract: ContractApiResponse | null | undefined) => boolean;
    isLoadingData: boolean;
    openInvoices: OpenInvoiceApiResponse[];
    isPayAll: boolean;
    selectedContractIds: string[];
    /** Needed to replicate the original page effect that expands payAll into selectable contract ids. */
    canSelectContract: (contractId: string) => boolean;
    t: (key: string) => string;
}

/**
 * Owns the payment-rows state and the handlers that build/mutate individual rows:
 * building/initializing rows, date/amount-choice changes, date-picker error mapping, and row deletion.
 */
export const usePaymentRows = ({
    activeContracts,
    getContractById,
    getTotalAmountForContract,
    getScheduledAmountForContract,
    prefillAmounts,
    singleInvoiceOverrides = {},
    isConsumerLoanContract,
    isLoadingData,
    openInvoices,
    isPayAll,
    selectedContractIds,
    canSelectContract,
    t,
}: UsePaymentRowsParams) => {
    const dispatch = useDispatch();

    const [paymentRows, setPaymentRows] = useState<PaymentRow[]>([]);
    const [, setSelectedPaymentContracts] = useState<string[]>([]);
    /**
     * The contracts the user has explicitly selected, tracked independently of `paymentRows`.
     * Switching to "Card" filters card-ineligible contracts out of `paymentRows` (so they aren't
     * paid by card), but that must NOT erase the user's selection — otherwise switching back to
     * "Bank Account" leaves those contracts unchecked. This ref preserves the intent so bank rows
     * can be restored.
     */
    const userSelectedContractIdsRef = useRef<string[]>([]);
    /** Per-row validation errors when user clicks Pay with missing/invalid fields (bank account, payment date, amount > 0). */
    const [paymentRowErrors, setPaymentRowErrors] = useState<
        Array<{ bankAccount: boolean; paymentDate: boolean; paymentAmount: boolean }>
    >([]);
    /** Per-row backend validation messages returned by the bulk payment submit APIs (e.g. duplicate payment, amount exceeds invoice). */
    const [paymentSubmitErrors, setPaymentSubmitErrors] = useState<Array<string | null>>([]);
    /** Per-row DatePicker parsing/range errors from MUI (kept separate from committed Dayjs value). */
    const [paymentDatePickerErrors, setPaymentDatePickerErrors] = useState<string[]>([]);
    /** Consumer loan "Other" amount inputs — focused after switching from Full Amount. */
    const otherAmountInputRefs = useRef<(HTMLInputElement | null)[]>([]);
    const [focusOtherAmountRowIndex, setFocusOtherAmountRowIndex] = useState<number | null>(null);

    useLayoutEffect(() => {
        if (focusOtherAmountRowIndex === null) return;
        const el = otherAmountInputRefs.current[focusOtherAmountRowIndex];
        el?.focus();
        // Reset the one-shot focus trigger now that the DOM focus has been applied.
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setFocusOtherAmountRowIndex(null);
    }, [focusOtherAmountRowIndex]);

    // Single source of truth for building a payment row for a contract. Used by the
    // initial setup, the manual select toggle, Select All, and (importantly) when
    // restoring rows that were filtered out while paying by card.
    const buildRow = useCallback(
        (contractId: string, useSingleInvoiceOverride: boolean): PaymentRow => {
            const contractObj = activeContracts.find(c => c.AspireId === contractId || c.Id?.toString() === contractId);
            // Use contractObj.Id as dbContractId if available, else fallback to AspireId
            const dbContractId =
                contractObj?.Id?.toString() || contractObj?.AspireId || contractObj?.dbContractId || contractId;
            // Use the Aspire ID from the contract object for lookups, fallback to passed contractId
            const aspireId = contractObj?.AspireId || contractId;
            const override = useSingleInvoiceOverride
                ? (singleInvoiceOverrides[contractId] ?? singleInvoiceOverrides[aspireId])
                : undefined;
            let totalAmount: number;
            let scheduledAmount: number;
            if (override) {
                totalAmount = parseFloat(override.dueAmount || '0.00');
                scheduledAmount = parseFloat(override.scheduledAmount || '0.00');
            } else {
                const contractData = getTotalAmountForContract(aspireId);
                totalAmount = parseFloat(contractData?.totalAmount || '0.00');
                // Try to get scheduled amount using both Aspire ID and DB ID
                scheduledAmount = parseFloat(getScheduledAmountForContract(aspireId) || '0.00');
                if (scheduledAmount === 0) {
                    scheduledAmount = parseFloat(getScheduledAmountForContract(dbContractId) || '0.00');
                }
            }
            // Keep exactly 2 decimal places as a number (no rounding, just truncate/format to 2 decimals)
            scheduledAmount = parseFloat(scheduledAmount.toFixed(2));
            const remainingDue = Math.max(0, totalAmount - scheduledAmount);
            // Use prefilled remaining due amount if available, otherwise use calculated remaining due
            const paymentAmount = prefillAmounts[contractId] || remainingDue.toFixed(2);
            const isConsumerLoan = isConsumerLoanContract(contractObj);
            return {
                contractId,
                dbContractId,
                bankAccount: '',
                paymentCardId: '',
                paymentDate: dayjs().startOf('day'),
                paymentAmount,
                dueAmount: totalAmount.toFixed(2),
                scheduledAmount,
                ...(isConsumerLoan && { amountChoice: 'invoice' as AmountChoice }),
            };
        },
        [
            activeContracts,
            getTotalAmountForContract,
            getScheduledAmountForContract,
            prefillAmounts,
            singleInvoiceOverrides,
            isConsumerLoanContract,
        ],
    );

    const buildPaymentRow = useCallback((contractId: string): PaymentRow => buildRow(contractId, true), [buildRow]);

    /**
     * Same as buildPaymentRow but always contract-wide (ignores singleInvoiceOverrides). Used for card
     * payments, which are submitted per contract and applied to the oldest open invoices.
     */
    const buildContractWidePaymentRow = useCallback(
        (contractId: string): PaymentRow => buildRow(contractId, false),
        [buildRow],
    );

    const initializePaymentRows = useCallback(
        (contractIds: string[]) => {
            if (contractIds?.length > 0) {
                const initialRows: PaymentRow[] = contractIds.map(buildPaymentRow);
                setPaymentRows(initialRows);
                setSelectedPaymentContracts(contractIds);
                userSelectedContractIdsRef.current = [...contractIds];
            }
        },
        [buildPaymentRow],
    );

    useEffect(() => {
        if (!isLoadingData && activeContracts.length > 0) {
            let contractsToInitialize = selectedContractIds;
            // If payAll is true, select all active contracts that can be selected
            if (isPayAll && activeContracts.length > 0) {
                contractsToInitialize = activeContracts
                    .map((contractItem: ContractApiResponse) => contractItem.AspireId || `Contract ${contractItem.Id}`)
                    .filter((contractId: string) => canSelectContract(contractId));
            }
            // Derives paymentRows from the resolved contract/invoice data — runs once data is ready.
            // eslint-disable-next-line react-hooks/set-state-in-effect
            initializePaymentRows(contractsToInitialize);
        }
    }, [
        isLoadingData,
        openInvoices,
        activeContracts,
        isPayAll,
        canSelectContract,
        initializePaymentRows,
        selectedContractIds,
    ]);

    const mapPaymentDatePickerReason = (reason: DatePickerValidationReason, meta?: { rawInput: string }): string => {
        if (reason === 'invalidDate') {
            return invalidDatePickerUserMessage(meta?.rawInput ?? '', 'generic', t);
        }
        if (reason === 'disableFuture') return t('datePicker.generic.disableFuture');
        if (reason === 'disablePast') return t('datePicker.generic.disablePast');
        if (reason === 'minDate') return t('datePicker.generic.minDate');
        if (reason === 'maxDate') return t('datePicker.generic.disableFuture');
        return t('datePicker.generic.invalid');
    };

    // Clears a row's backend submit-error message once the user edits that row, since they're now
    // retrying with different input rather than looking at the outcome of the failed attempt.
    const clearPaymentSubmitError = (index: number) => {
        setPaymentSubmitErrors(prev => {
            if (!prev[index]) return prev;
            const next = [...prev];
            next[index] = null;
            return next;
        });
    };

    const handleDateChange = (index: number, value: Dayjs | null) => {
        clearPaymentSubmitError(index);
        const today = dayjs().startOf('day');
        let validDate = value;

        if (value && value.isValid()) {
            const selectedDate = value.startOf('day');
            if (selectedDate.isBefore(today)) {
                validDate = null;
                dispatch(
                    openToast({
                        show: true,
                        isAlertOpen: true,
                        message: 'Payment Date Cannot Be in the Past. Please Select Today.',
                        severity: 'error',
                    }),
                );
            } else {
                // Payment date cannot be after contract maturity date + 90 days.
                // When maturity date isn't available, fall back to today + 90 days so this
                // never falls through to "no upper bound" (a contract without a MaturityDate
                // must not let a customer pick an arbitrary future date, e.g. year 2100).
                const row = paymentRows[index];
                const contract = row ? getContractById(row.contractId) || getContractById(row.dbContractId) : null;
                const maxAllowed = (contract?.MaturityDate ? dayjs(contract.MaturityDate) : today)
                    .add(90, 'day')
                    .startOf('day');
                if (selectedDate.isAfter(maxAllowed)) {
                    validDate = maxAllowed;
                    dispatch(
                        openToast({
                            show: true,
                            isAlertOpen: true,
                            message:
                                'Payment Date Cannot Be After Maturity Date Plus 90 Days. Date Has Been Set To The Maximum Allowed.',
                            severity: 'error',
                        }),
                    );
                }
            }
        }

        setPaymentDatePickerErrors(prev => {
            const next = [...prev];
            next[index] = '';
            return next;
        });
        setPaymentRowErrors(prev => {
            const next = [...prev];
            if (next[index]) next[index] = { ...next[index], paymentDate: !validDate };
            return next;
        });
        setPaymentRows(prev => {
            const updated = [...prev];
            updated[index].paymentDate = validDate;
            return updated;
        });
    };

    const handleDatePickerError = (index: number, reason: DatePickerValidationReason, meta?: { rawInput: string }) => {
        if (!reason) {
            setPaymentDatePickerErrors(prev => {
                const next = [...prev];
                next[index] = '';
                return next;
            });
            // Picker error resolved: drop the sticky row flag too (a missing date still disables Pay via row.paymentDate).
            setPaymentRowErrors(prev => {
                if (!prev[index]?.paymentDate) return prev;
                const next = [...prev];
                next[index] = { ...next[index], paymentDate: false };
                return next;
            });
            return;
        }
        const message = mapPaymentDatePickerReason(reason, meta);
        setPaymentDatePickerErrors(prev => {
            const next = [...prev];
            next[index] = message;
            return next;
        });
        setPaymentRowErrors(prev => {
            const next = [...prev];
            if (!next[index]) {
                next[index] = { bankAccount: false, paymentDate: true, paymentAmount: false };
            } else {
                next[index] = { ...next[index], paymentDate: true };
            }
            return next;
        });
    };

    const handleDeleteRow = (index: number) => {
        setPaymentRows(prev => {
            const rowToDelete = prev[index];
            if (rowToDelete) {
                // Remove from selectedPaymentContracts
                setSelectedPaymentContracts(prevSelected =>
                    prevSelected.filter(contractId => contractId !== rowToDelete.contractId),
                );
                // Deliberate delete is a real deselection — drop it from the persisted
                // selection so it isn't restored when switching back to Bank Account.
                userSelectedContractIdsRef.current = userSelectedContractIdsRef.current.filter(
                    contractId => contractId !== rowToDelete.contractId,
                );
            }
            return prev.filter((_, i) => i !== index);
        });
        setPaymentRowErrors([]);
        setPaymentSubmitErrors(prev => prev.filter((_, i) => i !== index));
        setPaymentDatePickerErrors(prev => prev.filter((_, i) => i !== index));
    };

    const handleAmountChoiceChange = (index: number, choice: AmountChoice) => {
        clearPaymentSubmitError(index);
        setPaymentRows(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], amountChoice: choice };
            if (choice === 'invoice') {
                const dueAmountNum = parseFloat(String(updated[index].dueAmount ?? '0')) || 0;
                const scheduledAmountNum = parseFloat(String(updated[index].scheduledAmount ?? '0')) || 0;
                updated[index].paymentAmount = parseFloat(Math.max(0, dueAmountNum - scheduledAmountNum).toFixed(2));
            } else {
                // "Other": empty field + focus (no $0.00 placeholder / no extra click)
                updated[index].paymentAmount = '';
            }
            return updated;
        });
        if (choice === 'invoice') {
            setPaymentRowErrors(prev => {
                const next = [...prev];
                if (next[index]) next[index] = { ...next[index], paymentAmount: false };
                return next;
            });
        }
        if (choice === 'other') {
            setFocusOtherAmountRowIndex(index);
        }
    };

    /**
     * Effective amount for payload/validation: consumer loan + Full Amount (invoice) uses open-invoice due total.
     * When no open invoices exist, falls back to outstanding balance to allow principal-only payments.
     * Must match the RadioGroup default: `row.amountChoice ?? 'invoice'` — if amountChoice was never set on the row
     * but the UI shows Full Amount, we still use dueAmount; otherwise pay amount and UI disagree.
     */
    const getEffectivePaymentAmount = (row: PaymentRow, contract: ContractApiResponse | null | undefined): number => {
        const choice = row.amountChoice ?? 'invoice';
        if (isConsumerLoanContract(contract) && choice === 'invoice') {
            const dueAmountNum = parseFloat(String(row.dueAmount ?? '0')) || 0;
            const scheduledAmountNum = parseFloat(String(row.scheduledAmount ?? '0')) || 0;
            const invoicesExist = dueAmountNum > 0;
            const effectiveAmount = Math.max(0, dueAmountNum - scheduledAmountNum);

            if (invoicesExist) {
                return parseFloat(effectiveAmount.toFixed(2));
            }

            const outstandingBalance = parseFloat(String(contract?.OutstandingBalance ?? '0')) || 0;
            if (outstandingBalance > 0) {
                return parseFloat(Math.max(0, outstandingBalance - scheduledAmountNum).toFixed(2));
            }

            return 0;
        }
        return parseFloat(String(row.paymentAmount ?? '')) || 0;
    };

    /** Resolve database contract ID for API (Id). Prefer row.dbContractId; fallback to contract.Id from activeContracts. */
    const getDbContractIdForPayload = (row: PaymentRow): string => {
        if (row.dbContractId && String(row.dbContractId).trim() !== '') {
            return String(row.dbContractId).trim();
        }
        const contract = getContractById(row.contractId) || getContractById(row.dbContractId);
        return contract?.Id?.toString() ?? contract?.AspireId ?? row.contractId ?? '';
    };

    /** Registers the "Other amount" input DOM node for a row so it can be focused by index. */
    const setOtherAmountInputRef = useCallback((index: number, el: HTMLInputElement | null) => {
        otherAmountInputRefs.current[index] = el;
    }, []);

    return {
        paymentRows,
        setPaymentRows,
        setSelectedPaymentContracts,
        userSelectedContractIdsRef,
        paymentRowErrors,
        setPaymentRowErrors,
        paymentSubmitErrors,
        setPaymentSubmitErrors,
        clearPaymentSubmitError,
        paymentDatePickerErrors,
        setPaymentDatePickerErrors,
        setOtherAmountInputRef,
        focusOtherAmountRowIndex,
        buildPaymentRow,
        buildContractWidePaymentRow,
        initializePaymentRows,
        handleDateChange,
        mapPaymentDatePickerReason,
        handleDatePickerError,
        handleDeleteRow,
        handleAmountChoiceChange,
        getEffectivePaymentAmount,
        getDbContractIdForPayload,
    };
};
